import { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Breadcrumbs,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    Grid,
    InputLabel,
    IconButton,
    MenuItem,
    Paper,
    Stack,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material';
import {
    EditOutlined as EditIcon,
    ManageSearch as ReportLookupIcon,
} from '@mui/icons-material';
import {
    getGlobalThemesBySystem,
    getLineageReport,
    getLineageReports,
    getThemesBySystem,
    getWorkspaces,
    updateLineageReportThemes,
} from '../services/api';

const REPORT_LOOKUP_SYSTEMS = [
    { value: 'legacy', label: 'Legacy' },
    { value: 'catalyst', label: 'Catalyst' },
];

const COLUMN_TYPE_FILTERS = [
    { value: '', label: 'All Types' },
    { value: 'calculation', label: 'Calculation' },
    { value: 'join', label: 'Join' },
    { value: 'filter', label: 'Filter' },
    { value: 'base', label: 'Base / Other' },
];

const COLUMN_LAYOUT_USAGE_FILTERS = [
    { value: '', label: 'All Layout Usage' },
    { value: 'yes', label: 'Yes' },
    { value: 'no', label: 'No' },
];

const normalizeFieldType = (value) => {
    const normalized = String(value || '').trim().toLowerCase();
    if (!normalized) return 'unknown';
    if (normalized === 'derived' || normalized === 'calculation') return 'calculation';
    if (normalized === 'filter') return 'filter';
    if (normalized === 'join') return 'join';
    if (normalized === 'base') return 'base';
    return normalized;
};

const isLayoutYes = (value) => {
    if (value === true) return true;
    if (!value) return false;
    return ['yes', 'y', 'true', '1'].includes(String(value).trim().toLowerCase());
};

const getColumnTypeSortRank = (type) => {
    switch (normalizeFieldType(type)) {
        case 'calculation':
            return 0;
        case 'join':
            return 1;
        case 'filter':
            return 2;
        case 'base':
            return 3;
        default:
            return 4;
    }
};

const deriveColumnType = (row = {}) => {
    const marker = String(
        row?.['Report SQL / Filter']
        || row?.['report sql / filter']
        || row?.['Report SQL Filter']
        || row?.report_sql_filter
        || row?.['Type']
        || row?.type
        || row?.['Column Type']
        || row?.column_type
        || ''
    ).trim().toLowerCase();
    if (marker === 'filter') return 'Filter';
    if (marker === 'sql') return 'Base';
    if (marker === 'join') return 'Join';
    if (marker === 'calculation' || marker === 'derived') return 'Calculation';

    const columnType = normalizeFieldType(
        row?.['Column Type']
        || row?.column_type
        || row?.['Legacy Column Type']
        || row?.legacy_column_type
        || row?.type
    );
    if (columnType === 'calculation') return 'Calculation';
    if (columnType === 'filter') return 'Filter';
    if (columnType === 'join') return 'Join';
    if (columnType === 'base') return 'Base';

    const calculated = String(row?.Calculated || row?.calculated || row?.['Legacy Calculated'] || row?.legacy_calculated || '').trim().toLowerCase();
    if (['yes', 'y', 'true', '1'].includes(calculated)) {
        return 'Calculation';
    }

    return 'Base';
};

const normalizeReportColumns = (rows = []) => {
    const columns = [];
    rows.forEach((row) => {
        const logical = row?.['Report - Data Item Name'] || '';
        const physical = row?.['Physical Name'] || row?.['Physical name'] || '';
        const columnType = row?.['Column Type'] || row?.column_type || row?.['Legacy Column Type'] || row?.legacy_column_type || '';
        const type = deriveColumnType(row);
        const usedInLayout = isLayoutYes(
            row?.['Report Layout']
            || row?.['report layout']
            || row?.report_layout
            || row?.layout_used
        );
        columns.push({ logical, physical, columnType, type, usedInLayout });
    });
    columns.sort((a, b) => {
        if (a.usedInLayout !== b.usedInLayout) {
            return Number(b.usedInLayout) - Number(a.usedInLayout);
        }
        const aRank = getColumnTypeSortRank(a.type);
        const bRank = getColumnTypeSortRank(b.type);
        if (aRank !== bRank) {
            return aRank - bRank;
        }
        const aType = String(a.type || '').toLowerCase();
        const bType = String(b.type || '').toLowerCase();
        if (aType !== bType) {
            return aType.localeCompare(bType);
        }
        const aLogical = (a.logical || '').toLowerCase();
        const bLogical = (b.logical || '').toLowerCase();
        if (aLogical !== bLogical) {
            return aLogical.localeCompare(bLogical);
        }
        const aPhysical = (a.physical || '').toLowerCase();
        const bPhysical = (b.physical || '').toLowerCase();
        return aPhysical.localeCompare(bPhysical);
    });
    return columns;
};

function ReportLookupPage() {
    const [workspaces, setWorkspaces] = useState([]);
    const [workspacesLoading, setWorkspacesLoading] = useState(true);
    const [selectedWorkspace, setSelectedWorkspace] = useState(null);
    const [classificationReports, setClassificationReports] = useState({ legacy: [], catalyst: [] });
    const [classificationLoading, setClassificationLoading] = useState(false);
    const [reportLookupSystem, setReportLookupSystem] = useState('legacy');
    const [reportLookupReport, setReportLookupReport] = useState(null);
    const [reportLookupDetail, setReportLookupDetail] = useState(null);
    const [reportLookupLoading, setReportLookupLoading] = useState(false);
    const [logicalColumnFilter, setLogicalColumnFilter] = useState('');
    const [physicalColumnFilter, setPhysicalColumnFilter] = useState('');
    const [typeColumnFilter, setTypeColumnFilter] = useState('');
    const [layoutUsageFilter, setLayoutUsageFilter] = useState('');
    const [error, setError] = useState(null);
    const [notice, setNotice] = useState(null);
    const [themeOptionsLoading, setThemeOptionsLoading] = useState(false);
    const [localThemeOptions, setLocalThemeOptions] = useState([]);
    const [globalThemeOptions, setGlobalThemeOptions] = useState([]);
    const [themeEditorKind, setThemeEditorKind] = useState(null);
    const [draftThemes, setDraftThemes] = useState([]);
    const [draftGlobalTheme, setDraftGlobalTheme] = useState(null);
    const [themeSaving, setThemeSaving] = useState(false);

    useEffect(() => {
        const loadWorkspaces = async () => {
            try {
                setWorkspacesLoading(true);
                const data = await getWorkspaces();
                const workspaceList = Array.isArray(data) ? data : [];
                setWorkspaces(workspaceList);

                const defaultWorkspace = workspaceList.find((item) => item?.lineage_file_id && item?.is_classified)
                    || workspaceList.find((item) => item?.lineage_file_id)
                    || null;
                setSelectedWorkspace(defaultWorkspace);
                setError(null);
            } catch (err) {
                setError(err.response?.data?.detail || 'Failed to load workspaces.');
            } finally {
                setWorkspacesLoading(false);
            }
        };

        loadWorkspaces();
    }, []);

    useEffect(() => {
        const loadThemeOptions = async () => {
            try {
                setThemeOptionsLoading(true);
                const [localData, globalData] = await Promise.all([
                    getThemesBySystem(reportLookupSystem),
                    getGlobalThemesBySystem(reportLookupSystem),
                ]);
                setLocalThemeOptions(Object.keys(localData?.themes || {}).sort((a, b) => a.localeCompare(b)));
                setGlobalThemeOptions(Object.keys(globalData?.themes || {}).sort((a, b) => a.localeCompare(b)));
                setError(null);
            } catch (err) {
                setLocalThemeOptions([]);
                setGlobalThemeOptions([]);
                setError(err.response?.data?.detail || 'Failed to load theme options.');
            } finally {
                setThemeOptionsLoading(false);
            }
        };

        loadThemeOptions();
    }, [reportLookupSystem]);

    useEffect(() => {
        const loadClassificationReports = async () => {
            if (!selectedWorkspace?.lineage_file_id || !selectedWorkspace?.is_classified) {
                setClassificationLoading(false);
                setClassificationReports({ legacy: [], catalyst: [] });
                return;
            }

            try {
                setClassificationLoading(true);
                const [legacyData, catalystData] = await Promise.all([
                    getLineageReports(selectedWorkspace.lineage_file_id, 'legacy'),
                    getLineageReports(selectedWorkspace.lineage_file_id, 'catalyst'),
                ]);
                setClassificationReports({
                    legacy: legacyData?.reports || [],
                    catalyst: catalystData?.reports || [],
                });
                setError(null);
            } catch (err) {
                setClassificationReports({ legacy: [], catalyst: [] });
                setError(err.response?.data?.detail || 'Failed to load reports for the selected workspace.');
            } finally {
                setClassificationLoading(false);
            }
        };

        setReportLookupReport(null);
        setReportLookupDetail(null);
        setThemeEditorKind(null);
        setLogicalColumnFilter('');
        setPhysicalColumnFilter('');
        setTypeColumnFilter('');
        setLayoutUsageFilter('');
        setNotice(null);
        loadClassificationReports();
    }, [selectedWorkspace]);

    useEffect(() => {
        const reports = classificationReports[reportLookupSystem] || [];
        if (!reportLookupReport) return;
        const stillExists = reports.some((report) => report?.report_name === reportLookupReport?.report_name);
        if (!stillExists) {
            setReportLookupReport(null);
            setReportLookupDetail(null);
        }
    }, [classificationReports, reportLookupReport, reportLookupSystem]);

    const workspaceStatus = useMemo(() => {
        if (!selectedWorkspace) return null;
        if (!selectedWorkspace.lineage_file_id) {
            return {
                severity: 'warning',
                message: 'This workspace does not have a lineage file yet.',
            };
        }
        if (!selectedWorkspace.is_classified) {
            return {
                severity: 'info',
                message: 'This workspace is not classified yet. Report Lookup becomes available after classification.',
            };
        }
        return {
            severity: 'success',
            message: 'Workspace is ready for report lookup.',
        };
    }, [selectedWorkspace]);

    const filteredColumns = useMemo(() => {
        const columns = reportLookupDetail?.columns || [];
        const logicalFilter = logicalColumnFilter.trim().toLowerCase();
        const physicalFilter = physicalColumnFilter.trim().toLowerCase();
        const typeFilter = typeColumnFilter.trim().toLowerCase();
        const layoutFilter = layoutUsageFilter.trim().toLowerCase();

        return columns.filter((column) => {
            const logicalValue = String(column?.logical || '').toLowerCase();
            const physicalValue = String(column?.physical || '').toLowerCase();
            const typeValue = String(column?.type || '').toLowerCase();
            const layoutValue = column?.usedInLayout ? 'yes' : 'no';
            const matchesLogical = !logicalFilter || logicalValue.includes(logicalFilter);
            const matchesPhysical = !physicalFilter || physicalValue.includes(physicalFilter);
            const matchesType = !typeFilter || typeValue === typeFilter;
            const matchesLayout = !layoutFilter || layoutValue === layoutFilter;
            return matchesLogical && matchesPhysical && matchesType && matchesLayout;
        });
    }, [layoutUsageFilter, logicalColumnFilter, physicalColumnFilter, reportLookupDetail, typeColumnFilter]);

    const handleLookupReport = async (system, report) => {
        if (!selectedWorkspace?.lineage_file_id) return;
        if (!report) {
            setReportLookupDetail(null);
            setThemeEditorKind(null);
            setLogicalColumnFilter('');
            setPhysicalColumnFilter('');
            setTypeColumnFilter('');
            setLayoutUsageFilter('');
            return;
        }

        try {
            setReportLookupLoading(true);
            const reportData = await getLineageReport(selectedWorkspace.lineage_file_id, system, report.report_name);
            setReportLookupDetail({
                report_name: reportData?.report_name || report.report_name,
                column_count: reportData?.column_count ?? report.column_count,
                themes: reportData?.themes || report.themes || [],
                global_themes: reportData?.global_themes || report.global_themes || [],
                columns: normalizeReportColumns(reportData?.columns || []),
            });
            setLogicalColumnFilter('');
            setPhysicalColumnFilter('');
            setTypeColumnFilter('');
            setLayoutUsageFilter('');
            setError(null);
        } catch (err) {
            setReportLookupDetail(null);
            setError(err.response?.data?.detail || 'Failed to load report details.');
        } finally {
            setReportLookupLoading(false);
        }
    };

    const handleOpenThemeEditor = (kind) => {
        if (!reportLookupDetail) return;
        setThemeEditorKind(kind);
        setDraftThemes(Array.isArray(reportLookupDetail.themes) ? reportLookupDetail.themes : []);
        setDraftGlobalTheme(reportLookupDetail.global_themes?.[0] || null);
        setNotice(null);
    };

    const handleCloseThemeEditor = () => {
        if (themeSaving) return;
        setThemeEditorKind(null);
    };

    const handleSaveThemes = async () => {
        if (!selectedWorkspace?.lineage_file_id || !reportLookupDetail?.report_name) {
            return;
        }

        try {
            setThemeSaving(true);
            const response = await updateLineageReportThemes(
                selectedWorkspace.lineage_file_id,
                reportLookupSystem,
                reportLookupDetail.report_name,
                {
                    themes: draftThemes,
                    global_theme: draftGlobalTheme || null,
                },
            );

            setReportLookupDetail((current) => (
                current
                    ? {
                        ...current,
                        themes: response?.themes || [],
                        global_themes: response?.global_themes || [],
                    }
                    : current
            ));
            setClassificationReports((current) => ({
                ...current,
                [reportLookupSystem]: (current?.[reportLookupSystem] || []).map((report) => (
                    report?.report_name === reportLookupDetail.report_name
                        ? {
                            ...report,
                            themes: response?.themes || [],
                            global_themes: response?.global_themes || [],
                        }
                        : report
                )),
            }));
            setNotice({
                severity: 'success',
                message: response?.message || 'Themes updated successfully.',
            });
            setError(null);
            setThemeEditorKind(null);
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to update report themes.');
        } finally {
            setThemeSaving(false);
        }
    };

    return (
        <Box>
            <Breadcrumbs sx={{ mb: 2 }}>
                <Typography color="text.secondary">Report Matching</Typography>
                <Typography color="text.primary">Report Lookup</Typography>
            </Breadcrumbs>

            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
                <ReportLookupIcon color="primary" />
                <Box>
                    <Typography variant="h4" fontWeight={700}>Report Lookup</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Search a classified workspace report to view its local themes, global themes, and columns.
                    </Typography>
                </Box>
            </Stack>

            {error && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {notice && (
                <Alert severity={notice.severity} sx={{ mb: 3 }} onClose={() => setNotice(null)}>
                    {notice.message}
                </Alert>
            )}

            <Card sx={{ mb: 3, borderRadius: 3 }}>
                <CardContent>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} md={7}>
                            <Autocomplete
                                options={workspaces}
                                loading={workspacesLoading}
                                value={selectedWorkspace}
                                onChange={(event, value) => setSelectedWorkspace(value)}
                                getOptionLabel={(option) => option?.name || ''}
                                isOptionEqualToValue={(option, value) => option?.id === value?.id}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Workspace"
                                        placeholder="Select workspace..."
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} md={5}>
                            {workspaceStatus ? (
                                <Alert severity={workspaceStatus.severity}>
                                    {workspaceStatus.message}
                                </Alert>
                            ) : (
                                <Alert severity="info">
                                    Select a workspace to start lookup.
                                </Alert>
                            )}
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            <Card sx={{ borderRadius: 3 }}>
                <CardContent>
                    <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ xs: 'flex-start', md: 'center' }} justifyContent="space-between" spacing={2} sx={{ mb: 2 }}>
                        <Box>
                            <Typography variant="h6" fontWeight={700}>Lookup</Typography>
                            <Typography variant="body2" color="text.secondary">
                                Search in the selected workspace classification output.
                            </Typography>
                        </Box>
                        <ToggleButtonGroup
                            value={reportLookupSystem}
                            exclusive
                            onChange={(event, value) => {
                                if (!value) return;
                                setReportLookupSystem(value);
                                setReportLookupReport(null);
                                setReportLookupDetail(null);
                                setThemeEditorKind(null);
                                setLogicalColumnFilter('');
                                setPhysicalColumnFilter('');
                                setTypeColumnFilter('');
                                setLayoutUsageFilter('');
                                setNotice(null);
                            }}
                            size="small"
                        >
                            {REPORT_LOOKUP_SYSTEMS.map((item) => (
                                <ToggleButton key={item.value} value={item.value}>{item.label}</ToggleButton>
                            ))}
                        </ToggleButtonGroup>
                    </Stack>

                    <Autocomplete
                        options={classificationReports[reportLookupSystem] || []}
                        loading={classificationLoading}
                        getOptionLabel={(option) => option?.report_name || ''}
                        value={reportLookupReport}
                        onChange={(event, value) => {
                            setReportLookupReport(value);
                            handleLookupReport(reportLookupSystem, value);
                        }}
                        isOptionEqualToValue={(option, value) => option?.report_name === value?.report_name}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                placeholder={`Search ${reportLookupSystem} reports...`}
                                fullWidth
                            />
                        )}
                        disabled={!selectedWorkspace?.lineage_file_id || !selectedWorkspace?.is_classified}
                        sx={{ mb: 3 }}
                    />

                    {classificationLoading && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                            <CircularProgress size={24} />
                        </Box>
                    )}

                    {!classificationLoading && !reportLookupLoading && selectedWorkspace?.is_classified && (classificationReports[reportLookupSystem] || []).length === 0 && (
                        <Alert severity="info" sx={{ mb: 3 }}>
                            No {reportLookupSystem} reports were found in this workspace.
                        </Alert>
                    )}

                    {reportLookupLoading && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                            <CircularProgress size={24} />
                        </Box>
                    )}

                    {!reportLookupLoading && reportLookupDetail && (
                        <Stack spacing={3}>
                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
                                    gap: 3,
                                }}
                            >
                                <Paper
                                    variant="outlined"
                                    sx={{
                                        p: 2,
                                        borderRadius: 3,
                                        minWidth: 0,
                                        height: '100%',
                                        position: 'relative',
                                        overflow: 'hidden',
                                        borderColor: 'rgba(25, 118, 210, 0.16)',
                                        bgcolor: 'rgba(255, 255, 255, 0.92)',
                                        boxShadow: '0 10px 30px rgba(15, 23, 42, 0.08)',
                                        backdropFilter: 'blur(8px)',
                                        '&::before': {
                                            content: '""',
                                            position: 'absolute',
                                            inset: 0,
                                            background: 'linear-gradient(135deg, rgba(255,255,255,0.85) 0%, rgba(255,255,255,0.35) 45%, rgba(255,255,255,0.15) 100%)',
                                            pointerEvents: 'none',
                                        },
                                    }}
                                >
                                    <Box sx={{ position: 'relative', zIndex: 1 }}>
                                        <Typography variant="subtitle2" fontWeight={700} gutterBottom>Report</Typography>
                                        <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                                            {reportLookupDetail.report_name}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                                            Columns: {reportLookupDetail.column_count || 0}
                                        </Typography>
                                    </Box>
                                </Paper>

                                <Paper
                                    variant="outlined"
                                    sx={{
                                        p: 2,
                                        borderRadius: 3,
                                        minWidth: 0,
                                        height: '100%',
                                        position: 'relative',
                                        overflow: 'hidden',
                                        borderColor: 'rgba(25, 118, 210, 0.16)',
                                        bgcolor: 'rgba(255, 255, 255, 0.92)',
                                        boxShadow: '0 10px 30px rgba(15, 23, 42, 0.08)',
                                        backdropFilter: 'blur(8px)',
                                        '&::before': {
                                            content: '""',
                                            position: 'absolute',
                                            inset: 0,
                                            background: 'linear-gradient(135deg, rgba(255,255,255,0.85) 0%, rgba(255,255,255,0.35) 45%, rgba(255,255,255,0.15) 100%)',
                                            pointerEvents: 'none',
                                        },
                                    }}
                                >
                                    <Box sx={{ position: 'relative', zIndex: 1 }}>
                                        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mb: 1 }}>
                                            <Typography variant="subtitle2" fontWeight={700}>Local Themes</Typography>
                                            <Tooltip title="Edit local themes">
                                                <span>
                                                    <IconButton
                                                        size="small"
                                                        color="primary"
                                                        onClick={() => handleOpenThemeEditor('local')}
                                                        disabled={themeOptionsLoading}
                                                        sx={{
                                                            bgcolor: 'rgba(25, 118, 210, 0.08)',
                                                            '&:hover': { bgcolor: 'rgba(25, 118, 210, 0.16)' },
                                                        }}
                                                    >
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        </Stack>
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, minWidth: 0 }}>
                                            {(reportLookupDetail.themes || []).map((theme) => (
                                                <Chip
                                                    key={theme}
                                                    label={theme}
                                                    size="small"
                                                    color="primary"
                                                    variant="outlined"
                                                    sx={{ maxWidth: '100%' }}
                                                />
                                            ))}
                                            {(reportLookupDetail.themes || []).length === 0 && (
                                                <Typography variant="body2" color="text.secondary">None</Typography>
                                            )}
                                        </Box>
                                    </Box>
                                </Paper>

                                <Paper
                                    variant="outlined"
                                    sx={{
                                        p: 2,
                                        borderRadius: 3,
                                        minWidth: 0,
                                        height: '100%',
                                        position: 'relative',
                                        overflow: 'hidden',
                                        borderColor: 'rgba(25, 118, 210, 0.16)',
                                        bgcolor: 'rgba(255, 255, 255, 0.92)',
                                        boxShadow: '0 10px 30px rgba(15, 23, 42, 0.08)',
                                        backdropFilter: 'blur(8px)',
                                        '&::before': {
                                            content: '""',
                                            position: 'absolute',
                                            inset: 0,
                                            background: 'linear-gradient(135deg, rgba(255,255,255,0.85) 0%, rgba(255,255,255,0.35) 45%, rgba(255,255,255,0.15) 100%)',
                                            pointerEvents: 'none',
                                        },
                                    }}
                                >
                                    <Box sx={{ position: 'relative', zIndex: 1 }}>
                                        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mb: 1 }}>
                                            <Typography variant="subtitle2" fontWeight={700}>Global Themes</Typography>
                                            <Tooltip title="Edit global theme">
                                                <span>
                                                    <IconButton
                                                        size="small"
                                                        color="secondary"
                                                        onClick={() => handleOpenThemeEditor('global')}
                                                        disabled={themeOptionsLoading}
                                                        sx={{
                                                            bgcolor: 'rgba(156, 39, 176, 0.08)',
                                                            '&:hover': { bgcolor: 'rgba(156, 39, 176, 0.16)' },
                                                        }}
                                                    >
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        </Stack>
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, minWidth: 0 }}>
                                            {(reportLookupDetail.global_themes || []).map((theme) => (
                                                <Chip
                                                    key={theme}
                                                    label={theme}
                                                    size="small"
                                                    color="secondary"
                                                    variant="outlined"
                                                    sx={{ maxWidth: '100%' }}
                                                />
                                            ))}
                                            {(reportLookupDetail.global_themes || []).length === 0 && (
                                                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 0 }}>
                                                    None
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>
                                </Paper>
                            </Box>

                            <Box>
                                <Stack
                                    direction={{ xs: 'column', md: 'row' }}
                                    spacing={1.5}
                                    justifyContent="flex-end"
                                    alignItems={{ xs: 'stretch', md: 'center' }}
                                    sx={{ mb: 1.5 }}
                                >
                                    <TextField
                                        size="small"
                                        label="Logical Name"
                                        placeholder="Search logical name..."
                                        value={logicalColumnFilter}
                                        onChange={(event) => setLogicalColumnFilter(event.target.value)}
                                        sx={{ minWidth: { xs: '100%', md: 220 } }}
                                    />
                                    <TextField
                                        size="small"
                                        label="Physical Name"
                                        placeholder="Search physical name..."
                                        value={physicalColumnFilter}
                                        onChange={(event) => setPhysicalColumnFilter(event.target.value)}
                                        sx={{ minWidth: { xs: '100%', md: 220 } }}
                                    />
                                    <FormControl size="small" sx={{ minWidth: { xs: '100%', md: 220 } }}>
                                        <InputLabel id="type-column-filter-label">Type</InputLabel>
                                        <Select
                                            labelId="type-column-filter-label"
                                            label="Type"
                                            value={typeColumnFilter}
                                            onChange={(event) => setTypeColumnFilter(event.target.value)}
                                        >
                                            {COLUMN_TYPE_FILTERS.map((item) => (
                                                <MenuItem key={item.value || 'all'} value={item.value}>
                                                    {item.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    <FormControl size="small" sx={{ minWidth: { xs: '100%', md: 220 } }}>
                                        <InputLabel id="layout-usage-filter-label">Used in Layout</InputLabel>
                                        <Select
                                            labelId="layout-usage-filter-label"
                                            label="Used in Layout"
                                            value={layoutUsageFilter}
                                            onChange={(event) => setLayoutUsageFilter(event.target.value)}
                                        >
                                            {COLUMN_LAYOUT_USAGE_FILTERS.map((item) => (
                                                <MenuItem key={item.value || 'all'} value={item.value}>
                                                    {item.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Stack>

                                <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 420, borderRadius: 2 }}>
                                    <Table size="small" stickyHeader>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Logical Name</TableCell>
                                                <TableCell>Physical Name</TableCell>
                                                <TableCell>Type</TableCell>
                                                <TableCell>Used in Report Layout</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {filteredColumns.map((column, index) => (
                                                <TableRow key={`${column.logical}-${column.physical}-${index}`}>
                                                    <TableCell sx={{ verticalAlign: 'top', wordBreak: 'break-word' }}>
                                                        {column.logical || '-'}
                                                    </TableCell>
                                                    <TableCell sx={{ verticalAlign: 'top', wordBreak: 'break-word' }}>
                                                        {column.physical || '-'}
                                                    </TableCell>
                                                    <TableCell sx={{ verticalAlign: 'top', wordBreak: 'break-word' }}>
                                                        <Chip
                                                            label={column.type === 'Base' ? '-' : (column.type || '-')}
                                                            size="small"
                                                            variant="outlined"
                                                            color={
                                                                column.type === 'Calculation'
                                                                    ? 'secondary'
                                                                    : column.type === 'Join'
                                                                        ? 'info'
                                                                        : column.type === 'Filter'
                                                                            ? 'warning'
                                                                            : 'default'
                                                            }
                                                        />
                                                    </TableCell>
                                                    <TableCell sx={{ verticalAlign: 'top', wordBreak: 'break-word' }}>
                                                        <Chip
                                                            label={column.usedInLayout ? 'Yes' : 'No'}
                                                            size="small"
                                                            variant={column.usedInLayout ? 'filled' : 'outlined'}
                                                            color={column.usedInLayout ? 'success' : 'default'}
                                                        />
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                            {filteredColumns.length === 0 && (
                                                <TableRow>
                                                    <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                                                        <Typography variant="body2" color="text.secondary">
                                                            {reportLookupDetail.columns?.length
                                                                ? 'No columns match the current search.'
                                                                : 'No columns found.'}
                                                        </Typography>
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Box>
                        </Stack>
                    )}
                </CardContent>
            </Card>

            <Dialog
                open={Boolean(themeEditorKind)}
                onClose={handleCloseThemeEditor}
                fullWidth
                maxWidth="sm"
                PaperProps={{
                    sx: {
                        borderRadius: 4,
                        overflow: 'hidden',
                        background: 'linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(246,249,252,0.98) 100%)',
                        boxShadow: '0 28px 70px rgba(15, 23, 42, 0.22)',
                    },
                }}
            >
                <DialogTitle sx={{ pb: 1.5 }}>
                    <Stack spacing={0.75}>
                        <Typography variant="h6" fontWeight={700}>
                            {themeEditorKind === 'global' ? 'Edit Global Theme' : 'Edit Local Themes'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {reportLookupDetail?.report_name || '-'}
                        </Typography>
                    </Stack>
                </DialogTitle>
                <DialogContent sx={{ pt: 1 }}>
                    <Paper
                        variant="outlined"
                        sx={{
                            p: 2,
                            borderRadius: 3,
                            mb: 2,
                            background: 'linear-gradient(135deg, rgba(25, 118, 210, 0.06) 0%, rgba(255,255,255,0.9) 100%)',
                            borderColor: 'rgba(25, 118, 210, 0.16)',
                        }}
                    >
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {themeEditorKind === 'global'
                                ? 'Select one global theme for this report. Clear the selection if the report should not have a global theme.'
                                : 'Select one or more local themes for this report. Duplicate selections are removed automatically.'}
                        </Typography>

                        {themeEditorKind === 'global' ? (
                            <Autocomplete
                                options={globalThemeOptions}
                                loading={themeOptionsLoading}
                                value={draftGlobalTheme}
                                onChange={(event, value) => setDraftGlobalTheme(value || null)}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Global Theme"
                                        placeholder="Choose global theme"
                                    />
                                )}
                                clearOnEscape
                                autoHighlight
                            />
                        ) : (
                            <Autocomplete
                                multiple
                                options={localThemeOptions}
                                loading={themeOptionsLoading}
                                value={draftThemes}
                                onChange={(event, value) => setDraftThemes(value)}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Local Themes"
                                        placeholder="Choose local themes"
                                    />
                                )}
                                filterSelectedOptions
                                autoHighlight
                            />
                        )}
                    </Paper>

                    <Paper
                        variant="outlined"
                        sx={{
                            p: 2,
                            borderRadius: 3,
                            background: 'rgba(255,255,255,0.85)',
                            borderColor: 'rgba(15, 23, 42, 0.08)',
                        }}
                    >
                        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                            Preview
                        </Typography>
                        {themeEditorKind === 'global' ? (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {draftGlobalTheme ? (
                                    <Chip label={draftGlobalTheme} color="secondary" variant="outlined" />
                                ) : (
                                    <Typography variant="body2" color="text.secondary">No global theme selected.</Typography>
                                )}
                            </Box>
                        ) : (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {draftThemes.length ? (
                                    draftThemes.map((theme) => (
                                        <Chip key={theme} label={theme} color="primary" variant="outlined" />
                                    ))
                                ) : (
                                    <Typography variant="body2" color="text.secondary">No local themes selected.</Typography>
                                )}
                            </Box>
                        )}
                    </Paper>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 3, pt: 1 }}>
                    <Button onClick={handleCloseThemeEditor} disabled={themeSaving}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSaveThemes}
                        disabled={themeSaving || themeOptionsLoading}
                    >
                        {themeSaving ? 'Saving...' : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default ReportLookupPage;
